import { SubmissionError } from 'redux-form';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import bowser from 'bowser/es5';
import { store } from 'store';
import get from 'lodash.get';

// to fill BE data in lowercase to FE visual in capitalized
export const capitalizeString = str =>
  str && str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);

// takes a label
export const valueToLabel = value => (value ? value.split('_').join(' ') : '');
export const labelToValue = value =>
  value
    ? value
        .toLowerCase()
        .split(' ')
        .join('_')
    : '';

export const formatCoordinate = value =>
  isNaN(parseFloat(value)) ? value : parseFloat(value).toPrecision(8);

export const geoToObject = geoString => {
  const geoArray = geoString.split(',');
  return {
    lat: formatCoordinate(geoArray[0]),
    lng: formatCoordinate(geoArray[1]),
  };
};

// used for Diary Field Values (eg yield) - always get the last of array
export const getLatestDiary = arr =>
  arr && arr.length > 0 && arr[arr.length - 1];

// if the field has crops, that includes harvested crop, flip `harvested` to be true
// otherwise the field is not harvested by default
const isHarvested = expectedYield => {
  const latestYield = getLatestDiary(expectedYield);
  return latestYield && latestYield.yield && latestYield.yield.harvested;
};
export const getHarvestedFlag = crops => {
  // the field is harvested if there is no crop in the field that are not harvested
  const harvested =
    crops &&
    crops.length > 0 &&
    !crops.some(crop => !isHarvested(crop.expectedYield));
  return harvested;
};

// manually set redux-form error on graphQL error
export const graphQlToFormErrors = e => {
  // we actually don't know wht message we get for specification
  // CHANGE LATER: throwing the BE error as is for developers for now
  const message =
    e.graphQLErrors && e.graphQLErrors.find(error => error.message);
  const _error = (message && message.message) || e.toString();
  // const _error = message && message.message || 'Something went wrong. Please refresh the page and try again.';
  throw new SubmissionError({ _error });
};

/**
 *
 * Split field ID into season and fieldname
 * @function splitFieldName
 * @param {String} fieldId
 * @returns {Object}
 */
export const splitFieldName = fieldId => {
  const fieldArray = fieldId.split(':');
  const [, season, fieldName] = fieldArray;
  return { season: parseInt(season, 10), fieldName };
};

/**
 *
 *
 * @function splitCropFieldName
 * @param {String} cropFieldID
 * @returns {Object}
 */
export const splitCropFieldName = cropFieldID => {
  const cropFieldArray = cropFieldID.split(':');
  const [, season, crop, variety] = cropFieldArray;
  return { id: cropFieldID, season: parseInt(season, 10), crop, variety };
};

/**
 *
 *
 * @function getMonthNameFromDate
 * @param {String} - unix time stamp
 * @returns {String} - month name in MMM format for charts
 */
export const getMonthNameFromDate = timestamp =>
  moment(timestamp).format('MMM');

/**
 *
 *
 * @function getMonthNumberFromName
 * @param {String} - month name in MMM format
 * @returns {String} - month number for use in charts
 */
export const getMonthNumberFromName = monthName =>
  moment()
    .month(monthName)
    .format('M');

/**
 *
 *
 * @function getMonthNameFromNumber
 * @param {Number} - monthNum, oneOf 1 to 12
 * @returns {String} - MMM formated month name for use in charts
 */
export const getMonthNameFromNumber = monthNum =>
  moment()
    .month(monthNum)
    .format('MMM');

/**
 *
 *
 * @function isDesktop
 * @returns {Boolean}
 */
export const isDesktop = () => {
  const browser = bowser.getParser(window.navigator.userAgent);
  return browser.getPlatform().type === 'desktop';
};

/**
 *
 *
 * @function browserInfo
 * @returns {Object} - bowser object
 */
export const browserInfo = () => bowser.getParser(window.navigator.userAgent);

/**
 *
 *
 * @function isIE11
 * @returns {Boolean}
 */
export const isIE11 = () => {
  const browser = bowser.getParser(window.navigator.userAgent);
  return (
    browser.getBrowser().version === '11.0' &&
    browser.getBrowser().name === 'Internet Explorer'
  );
};

/**
 *
 *
 * @function isDev
 * @returns {Boolean}
 */
export const isDev = () => process.env.NODE_ENV === 'development';

/**
 *
 *
 * @function isProd
 * @returns {Boolean}
 */
export const isProd = () => process.env.NODE_ENV === 'production';

/**
 *
 *
 * @function sentryIsEnabled
 * @param {error} - optional param, is passed in via <ErrorBoundary />
 * @returns {Boolean}
 *
 * This function will do a check to make sure Raven is available for Sentry.
 */
export const sentryIsEnabled = (error = null) => {
  if (error) return !!(isDev() && window && window.Raven && error);
  return !!(isDev() && window && window.Raven);
};

/**
 * Sum up all elements in array
 *
 * @function sumArray
 * @returns {Array}
 *
 */
export const sumArray = ary =>
  ary && ary.length > 0
    ? ary.map(num => Number(num)).reduce((total, num) => total + num)
    : 0;

export const omitDeep = (obj, key) => {
  if (Array.isArray(obj)) return omitDeepArrayWalk(obj, key);
  const keys = Object.keys(obj);
  const newObj = {};
  keys.forEach(i => {
    if (i !== key) {
      const val = obj[i];
      if (Array.isArray(val)) newObj[i] = omitDeepArrayWalk(val, key);
      else if (typeof val === 'object' && val !== null)
        newObj[i] = omitDeep(val, key);
      else newObj[i] = val;
    }
  });
  return newObj;
};

/**
 * Helper function for omitDeep.
 * Returns an array after omitting the key.
 *
 * @function omitDeepArrayWalk
 * @returns {Array}
 */
const omitDeepArrayWalk = (arr, key) => {
  return arr.map(val => {
    if (Array.isArray(val)) return omitDeepArrayWalk(val, key);
    else if (typeof val === 'object') return omitDeep(val, key);
    return val;
  });
};

/**
 * Helper function for generating universal unique identification.
 * Returns a string.
 *
 * @function generateUUID
 * @returns {String}
 */
export const generateUUID = () => uuidv4();

/**
 * Helper function to register network status listening
 * Returns null.
 *
 * @function registerNetworkStatus
 * @returns {null}
 */
export const registerNetworkStatus = () => {
  // Handle network status change
  window.addEventListener(
    'load',
    () => {
      if (navigator.onLine) {
        store.dispatch({ type: 'UPDATE_NETWORK_STATUS', payload: true });
      } else {
        store.dispatch({ type: 'UPDATE_NETWORK_STATUS', payload: false });
      }
    },
    { once: true }
  );

  window.addEventListener(
    'online',
    () => {
      store.dispatch({ type: 'UPDATE_NETWORK_STATUS', payload: true });
    },
    false
  );

  window.addEventListener(
    'offline',
    () => {
      store.dispatch({ type: 'UPDATE_NETWORK_STATUS', payload: false });
    },
    false
  );
};

/**
 * Helper function to unregister network status listening
 * Returns null.
 *
 * @function unregisterNetworkStatus
 * @returns {null}
 */
export const unregisterNetworkStatus = () => {
  const _listener = function() {
    //
  };
  window.removeEventListener('load', _listener);
  window.removeEventListener('online', _listener);
  window.removeEventListener('offline', _listener);
};

/**
 * Helper function to retrieve connect status
 * Returns {Boolean}.
 *
 * @function unregisterNetworkStatus
 * @returns {Boolean}
 */
export const isConnected = () => {
  return store.getState().settings.isOnline;
};

/**
 * Helper function to get user position
 * Returns {Object}.
 *
 * @function getUserPostion
 * @returns {null}
 */
export const getUserPostion = fn => {
  navigator.geolocation.getCurrentPosition(fn);
};

/**
 * Remove duplicate object of same field name from array
 * Returns {Array}.
 *
 * @function getUserPostion
 * @returns {null}
 */
export const uniqBy = (arr = [], comp) => {
  if (arr.length === 0) {
    return [];
  }

  const unique = arr
    .map(e => get(e, comp))

    // store the keys of the unique objects
    .map((e, i, final) => final.indexOf(e) === i && i)

    // eliminate the dead keys & store unique objects
    .filter(e => arr[e])
    .map(e => arr[e]);

  return unique;
};

/**
 *
 * Group array by object key
 * @function groupBy
 * @param {Array} items
 * @param {String} key
 * @returns {Object}
 */
export const groupBy = (items, key) =>
  items.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {}
  );

/**
 *
 * Check if object is empty
 * @function isEmpty
 * @param {Object} obj
 * @returns {Boolean}
 */
export const isEmpty = obj => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
};

/**
 *
 * Sort object by key
 * @function sortByKey
 * @param {Object} obj
 * @param {Function} func
 * @returns {Object}
 */
export const sortByKey = (obj, func) => {
  const sortedObj = {};
  Object.keys(obj)
    .sort(func)
    .forEach(key => {
      sortedObj[key] = obj[key];
    });

  return sortedObj;
};

/**
 *
 * validate json string
 * @function isJsonString
 * @param {String} json
 */
export const isJsonString = json => {
  try {
    JSON.parse(json);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 *
 * convert json string to ojb
 * @function isJsonString
 * @param {String} json
 */
export const convertJsonToObj = json => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

/**
 *
 * Returns a promise that resolves after how many milliseconds you pass it.
 * @function wait
 * @param {Number} amount
 */
export const wait = amount =>
  new Promise(resolve => setTimeout(resolve, amount));



// WEBPACK FOOTER //
// ./src/common/utils.js